<template>
    <div class="bg-card rounded border-0">
        <div class="card-body p-0" style="height: 75vh; overflow-y: auto">
            <div v-if="error" class="d-flex justify-content-center py-5">
                <div class="text-danger">
                    {{ $trans.get('__JSON__.Failed to retrieve table resource') }}
                </div>
            </div>

            <div v-else-if="initializing" class="d-flex justify-content-center py-5">
                <div class="spinner-border" role="status">
                    <span class="visually-hidden">Initializing...</span>
                </div>
            </div>

            <div class="position-relative" v-else>
                <table class="table table-hover table-nowrap" :class="{ 'table-spaced': tableSpaced }">
                    <thead v-if="canSearch || canSort">
                    <tr>
                        <td :colspan="Math.ceil(headers.length / 2)" class="p-2">
                            <div class="input-group input-group-sm" v-if="search.visible">
                                <span class="input-group-text">
                                    <span class="spinner-border spinner-border-sm" role="status" v-if="fetching" />
                                    <i class="bi bi-search" v-else></i>
                                </span>
                                <input type="text" class="form-control form-control-sm" :placeholder="$trans.get('__JSON__.Searching within fields: :fields', { fields: searchableFields.toLowerCase() })" :disabled="fetching" v-model="search.value" @input="searchHandler" ref="search" />
                            </div>
                        </td>
                        <td :colspan="Math.ceil(headers.length / 2)" class="text-end p-2">
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-sm" :class="{ 'btn-neutral': !search.visible }" @click="toggleSearch" v-if="canSearch" :title="$trans.get('__JSON__.Search (F)')" tooltip>
                                    <span v-if="search.visible">{{ $trans.get('__JSON__.cancel') }}</span>
                                    <i class="bi bi-search" v-else></i>
                                </button>
                                <button class="btn btn-sm btn-neutral dropdown-toggle" role="button" data-bs-toggle="dropdown" v-if="canSort" :title="$trans.get('__JSON__.Sort')" tooltip>
                                    <i class="bi bi-arrow-down-up"></i>
                                </button>
                                <ul class="dropdown-menu dropdown-menu-sm" v-if="canSort">
                                    <li>
                                        <h6 class="dropdown-header">
                                            {{ $trans.get('__JSON__.Sort by') }}
                                        </h6>
                                    </li>
                                    <li class="dropdown-item d-flex align-items-center" v-for="item in sortableFields" :key="item.key">
                                        <div class="text-md">
                                            <input class="form-check-input" type="radio" :id="`sort_${item.key}`" :value="item.key" :checked="sort.column === item.key" v-model="sort.column" @change="sortColumnHandler(item.key)">
                                        </div>
                                        <div class="ms-2 me-5">
                                            <label :for="`sort_${item.key}`">
                                                {{ item.title }}
                                            </label>
                                        </div>
                                    </li>
                                    <li class="dropdown-divider"></li>
                                    <li class="px-1">
                                        <button class="btn btn-sm dropdown-item" :class="{'bg-soft-primary text-primary': this.sort.direction === 'asc'}" @click="sortDirectionHandler('asc')">
                                            <i class="bi bi-arrow-up"></i> A-Z
                                        </button>
                                    </li>
                                    <li class="px-1">
                                        <button class="btn btn-sm dropdown-item" :class="{'bg-soft-primary text-primary': this.sort.direction === 'desc'}" @click="sortDirectionHandler('desc')">
                                            <i class="bi bi-arrow-down"></i> Z-A
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </td>
                    </tr>
                    </thead>
                    <thead class="table-light" :class="{ 'sticky-top top-0': tableSticky }">
                    <tr>
                        <th scope="col" v-for="heading in headers">
                            {{ heading?.title }}
                        </th>
                    </tr>
                    </thead>
                    <tbody v-if="! hasRows">
                    <tr v-if="$slots.empty">
                        <th :colspan="headers.length" class="text-center">
                            <slot name="empty" />
                        </th>
                    </tr>
                    <tr v-else>
                        <th :colspan="headers.length" class="text-center">
                            <em>{{ $trans.get('__JSON__.No records found to show') }}</em>
                        </th>
                    </tr>
                    </tbody>
                    <tbody v-else>
                    <tr v-for="(item, idx) in paginated.data" :key="idx">
                        <td v-for="key in headingKeys" :class="{ 'text-end': key === 'actions' }">
                            <div class="btn-group" v-if="key === 'actions'">
                                <slot
                                    :name="`action(${actionKey(akey, value)})`"
                                    :action="actionKey(akey, value)"
                                    :value="value"
                                    :item="item"
                                    v-for="(value, akey) in item[key]"
                                >
                                    {{ value }}
                                </slot>
                            </div>

                            <slot
                                :name="`cell(${key})`"
                                :value="item[key]"
                                :item="item"
                                v-else
                            >
                                {{ item[key] }}
                            </slot>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div class="card-footer" v-if="hasPagination">
            <nav class="d-flex justify-items-center justify-content-between">
                <div class="d-flex justify-content-between flex-fill d-sm-none">
                    <ul class="pagination">
                        <li class="page-item" :class="{ 'disabled': ! paginated.prev_page_url }">
                            <a class="page-link" :href="paginated.prev_page_url" rel="prev" v-if="paginated.prev_page_url" :title="$trans.get('__JSON__.Previous (J)')" tooltip>
                                {{ $trans.get('__JSON__.« Previous') }}
                            </a>
                            <span class="page-link" v-else>
                                {{ $trans.get('__JSON__.« Previous') }}
                            </span>
                        </li>
                        <li class="page-item" :class="{ 'disabled': ! paginated.next_page_url }" :title="$trans.get('__JSON__.Next (K)')" tooltip>
                            <a class="page-link" :href="paginated.next_page_url" rel="next" v-if="paginated.next_page_url">
                                {{ $trans.get('__JSON__.Next »') }}
                            </a>
                            <span class="page-link" v-else>
                                {{ $trans.get('__JSON__.Next »') }}
                            </span>
                        </li>
                    </ul>
                </div>
                <div class="d-none flex-sm-fill d-sm-flex align-items-sm-center justify-content-sm-between">
                    <div>
                        <p class="small text-muted">
                            {{ $trans.get('__JSON__.Showing :from to :to of :total results', { from: paginated.from || 0, to: paginated.to || 0, total: paginated.total }) }}
                        </p>
                    </div>
                    <div>
                        <ul class="pagination">
                            <li class="page-item" :class="{ 'disabled': ! link.url || fetching, 'active': link.active }" v-for="link in paginated.links">
                                <span class="page-link" v-if="fetching && parseInt(link.label) === paginated.current_page">
                                    <span class="spinner-border spinner-border-sm" role="status" />
                                </span>
                                <span class="page-link" v-html="paginationLabel(link)" v-else-if="parseInt(link.label) === paginated.current_page"></span>
                                <button class="page-link" v-html="paginationLabel(link)" @click="gotTo(link)" :disabled="fetching" v-else-if="link.url" :title="paginationTooltip(link)" tooltip></button>
                                <span class="page-link" v-html="paginationLabel(link)" v-else></span>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>
        </div>
    </div>
</template>

<!--<style lang="scss" scoped>-->
<!--.sorting {-->
<!--    &:before {-->
<!--        right: 1em;-->
<!--        content: "\f0de";-->
<!--        font-family: 'Font Awesome\ 5 Free';-->
<!--        font-weight: 900;-->
<!--        font-size: 1rem;-->
<!--    }-->
<!--    &:after {-->
<!--        content: "\f0dd";-->
<!--        font-family: 'Font Awesome\ 5 Free';-->
<!--        font-weight: 900;-->
<!--        right: 16px;-->
<!--        font-size: 1rem;-->
<!--    }-->
<!--}-->
<!--</style>-->

<script>
import _debounce from 'lodash/debounce'

export default {
    name: 'IndexTable',
    props: {
        endpoint: {
            type: String,
            required: true,
        },
        tableSticky: {
            type: Boolean,
            required: false,
            default: true,
        },
        tableSpaced: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    computed: {
        headingKeys() {
            return this.headers.map((it, idx) => it.key || idx)
        },
        hasPagination() {
            if (this.initializing) return false
            else return this.paginated.total > this.paginated.per_page
        },
        hasRows() {
            return this.paginated.data.length > 0
        },
        searchableFields() {
            return this.headers.filter(it => it.searchable || false).map(it => it.title).join(', ')
        },
        sortableFields() {
            return this.headers.filter(it => it.sortable || false)
        },
        canSearch() {
            return this.searchableFields.length > 0
        },
        canSort() {
            return this.sortableFields.length > 0 && this.hasRows
        },
    },
    watch: {
        'paginated.data'() {
            this.initializeDataHelpers()
        },
    },
    data() {
        return {
            initializing: true,
            fetching: false,
            error: false,
            search: {
                visible: false,
                value: null,
            },
            headers: [],
            paginated: {
                data: [],
            },
            sort: {
                column: null,
                direction: 'asc',
            },
            tooltips: [],
            confirms: [],
        }
    },
    methods: {
        async fetch(page = null) {
            this.fetching = true

            const params = {
                page: page || this.paginated.current_page,
            }

            if (this.search.value) params.search = this.search.value
            if (this.sort.column) params.sort = `${this.sort.column},${this.sort.direction}`

            const response = await axios.get(this.endpoint, { params })

            this.headers = response.data.headers
            this.paginated = response.data.paginated
            this.sort = response.data.sort
            // TODO: add limit here as well :) so this will be handled via the backend

            if (this.headers === undefined) this.error = true

            this.fetching = false
        },
        paginationLabel(link) {
            let value = link.label
            if (value.includes('&laquo;')) value = '&lsaquo;'
            else if (value.includes('&raquo;')) value = '&rsaquo;'
            return value
        },
        paginationTooltip(link) {
            let value = link.label
            if (value.includes('&laquo;')) return this.$trans.get('__JSON__.Previous (J)')
            else if (value.includes('&raquo;')) return this.$trans.get('__JSON__.Next (K)')
            return undefined
        },
        async gotTo(link) {
            const query = link.url.split('?')[1]
            const params = new Proxy(new URLSearchParams(query), {
                get: (searchParams, prop) => searchParams.get(prop),
            })
            const page = parseInt(params.page)
            if (this.paginated.current_page === page) return
            this.paginated.current_page = page
            await this.fetch()
        },
        async toggleSearch() {
            this.search.visible = ! this.search.visible
            if (this.search.visible) setTimeout(() => this.$refs.search.focus(), 50)
            else {
                this.search.value = null
                await this.fetch()
            }
        },
        async reload() {
            await this.fetch()
        },
        keyboardListener() {
            window.addEventListener('keydown', async event => {
                if (['input', 'textarea'].includes(event.target.localName)) return

                // search
                if (! (event.metaKey || event.ctrlKey) && event.key === 'f') {
                    event.preventDefault()
                    await this.toggleSearch()
                }

                // reload page
                if (! (event.metaKey || event.ctrlKey) && event.key === 'r') {
                    event.preventDefault()
                    await this.reload()
                }

                // previous page
                if (! (event.metaKey || event.ctrlKey) && event.key === 'j') {
                    event.preventDefault()
                    if (this.paginated.current_page === 1) return
                    this.paginated.current_page = this.paginated.current_page - 1
                    await this.fetch()
                }

                // next page
                if (! (event.metaKey || event.ctrlKey) && event.key === 'k') {
                    event.preventDefault()
                    if (this.paginated.current_page === this.paginated.last_page) return
                    this.paginated.current_page = this.paginated.current_page + 1
                    await this.fetch()
                }
            })
        },
        initializeDataHelpers() {
            setTimeout(() => {
                this.tooltips = [].slice.call(document.querySelectorAll('[tooltip]'))
                    .map(element => new bootstrap.Tooltip(element));

                const confirmModal = new bootstrap.Modal(document.getElementById('confirmModal'))
                this.confirms = [].slice.call(document.querySelectorAll('[data-confirm]'))
                    .map(element => element.onclick = event => {
                        event.preventDefault()
                        confirmModal.show(element)
                    })
            }, 100)
        },
        searchHandler: _debounce(async function () {
            await this.fetch(1)
        }, 600),
        async sortColumnHandler(column) {
            this.sort.column = column
            await this.fetch()
        },
        async sortDirectionHandler(direction) {
            this.sort.direction = direction
            await this.fetch()
        },
        actionKey(key, value) {
            let output = key
            if (! isNaN(parseInt(key))) output = value
            return output
        },
    },
    async mounted() {
        await this.fetch()
        this.keyboardListener()
        this.$mitt.on('reloadIndexTable', event => this.reload())
        this.initializing = false
    },
}
</script>
